import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { COMPANY, IMAGES, FEATURE_CARDS, SERVICES, INDUSTRIES, WHY, DELIVERY_MODELS, CREDIBILITY_AREAS, HOW_IT_WORKS, TRUST_BAR } from "@/data/site";
import { Reveal, KineticHeading, Stagger, StaggerItem } from "@/components/Motion";
import { Overline, PrimaryButton, CTABand, SectionHeading } from "@/components/UI";
import SEO from "@/components/SEO";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy" data-testid="home-hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroAtlanta} alt="Atlanta skyline" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="absolute inset-0 bg-navy/25" />
      </motion.div>
      <div className="grain absolute inset-0 opacity-40" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 w-full pb-20 pt-36">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Overline>{COMPANY.type} · {COMPANY.hq}</Overline>
        </motion.div>

        <h1 className="mt-8 font-display font-900 tracking-tightest text-white text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-[5.2rem] max-w-5xl">
          <KineticHeading
            delay={0.35}
            lines={["A Consulting &", "Fractional Services Firm", "For Organizations That Lead"]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 text-white/75 font-body text-base md:text-lg leading-relaxed max-w-2xl"
        >
          We provide organizations with strategic workforce, operational, administrative, technology, marketing, training,
          security, and business support solutions, as consultants, embedded fractional leaders, or workforce partners.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-6 inline-flex items-center gap-3 border-l-2 border-gold pl-4"
          data-testid="hero-positioning"
        >
          <span className="text-gold text-xs font-700 uppercase tracking-[0.25em]">Four Ways We Deliver</span>
          <span className="text-white/60 text-sm hidden sm:inline">Consulting · Fractional · Workforce · Career Development</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3"
        >
          <Link to="/business-assessment" data-testid="hero-cta-assessment" className="inline-flex items-center justify-center gap-2 bg-gold text-navy font-700 px-7 py-4 hover:bg-white transition-colors duration-300 group">
            Take the Business Assessment <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </Link>
          <Link to="/contact" data-testid="hero-cta-consultation" className="inline-flex items-center justify-center gap-2 border border-white text-white font-600 px-7 py-4 hover:bg-white hover:text-navy transition-colors duration-300">
            Schedule a Consultation
          </Link>
          <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white/85 font-600 px-7 py-4 hover:border-white hover:text-white transition-colors duration-300">
            Explore Our Solutions
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-8"
        >
          <Link
            to="/business-assessment"
            data-testid="hero-assessment-link"
            className="group inline-flex items-center gap-3 text-white/80 text-sm hover:text-gold transition-colors"
          >
            <span className="w-8 h-px bg-gold/60 group-hover:w-14 group-hover:bg-gold transition-all duration-500" />
            <span>Not sure where to start? <span className="font-700 text-gold">Take the free 5-min assessment</span></span>
            <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="bg-navy border-t border-white/10" data-testid="home-trust-bar">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {TRUST_BAR.map((t, i) => (
          <React.Fragment key={t}>
            {i > 0 && <span className="text-gold/50 text-xs">|</span>}
            <span className="text-white/80 text-[11px] sm:text-xs font-700 uppercase tracking-[0.16em]">{t}</span>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function Practitioner() {
  return (
    <section className="bg-lightgray py-24 md:py-32" data-testid="home-practitioner">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="Credibility" title={<>Built on <span className="text-gold">Practitioner Experience</span></>} intro="Experienced leaders. Multidisciplinary expertise. Practical solutions." />
        <p className="mt-6 max-w-3xl text-navy/75 leading-relaxed">
          Consilium Pro is a Principal-led consulting and fractional services firm built by experienced practitioners across business, government, military, security, technology, training, communications, and operations.
        </p>
        <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {CREDIBILITY_AREAS.map((c, i) => (
            <StaggerItem key={c.title}>
              <div data-testid={`credibility-${i}`} className="bg-white p-8 h-full flex items-start gap-4">
                <span className="w-11 h-11 bg-navy flex items-center justify-center shrink-0">
                  <Icon name={c.icon} className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-display font-700 text-navy leading-snug">{c.title}</h3>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-navy py-24 md:py-32 relative overflow-hidden" data-testid="home-how-it-works">
      <div className="grain absolute inset-0 opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="How It Works" title="Assess. Diagnose. Design. Deliver. Measure." intro="A repeatable, practitioner-led delivery arc, the same discipline behind every engagement." light />
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {HOW_IT_WORKS.map((s, i) => (
            <StaggerItem key={s.step}>
              <div data-testid={`how-step-${s.step.toLowerCase()}`} className="bg-white/[0.04] border border-white/10 p-6 h-full hover:border-gold/60 transition-colors">
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-800 text-gold text-2xl">0{i + 1}</span>
                  <h3 className="font-display font-700 text-white text-lg tracking-tight">{s.step}</h3>
                </div>
                <p className="mt-4 text-white/70 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function DeliveryModels() {
  return (
    <section className="bg-white relative -mt-1" data-testid="home-delivery-models">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-3xl">
            <Overline>How We Deliver</Overline>
            <h2 className="mt-4 font-display font-800 tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.02]">
              Four flexible ways to <span className="text-gold">put our expertise to work</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed text-base md:text-lg max-w-2xl">
              Consilium Pro is a consulting and fractional services firm. Depending on what you need, we can advise, embed,
              staff, or coach, sometimes all four inside a single engagement.
            </p>
          </div>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {DELIVERY_MODELS.map((m, i) => (
            <StaggerItem key={m.slug}>
              <div data-testid={`delivery-model-${m.slug}`} className="group relative bg-white p-8 md:p-10 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 border border-slate-200 flex items-center justify-center group-hover:border-gold transition-colors">
                    <Icon name={m.icon} className="w-6 h-6 text-navy group-hover:text-gold transition-colors" strokeWidth={1.5} />
                  </div>
                  <span className="font-display font-800 text-4xl text-slate-100">{m.id}</span>
                </div>
                <h3 className="mt-6 font-display font-700 text-2xl text-navy leading-tight">{m.title}</h3>
                <p className="mt-1 text-gold text-sm font-600">{m.tagline}</p>
                <p className="mt-4 text-gray-600 leading-relaxed text-sm">{m.short}</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-[10px] font-700 text-navy/50 uppercase tracking-[0.16em] mb-2">Ideal for</p>
                  <p className="text-sm text-navy/80 leading-relaxed">{m.ideal}</p>
                </div>
                <div className="mt-5">
                  <p className="text-[10px] font-700 text-navy/50 uppercase tracking-[0.16em] mb-2">What's inside</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {m.includes.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-xs text-navy/75">
                        <span className="mt-1.5 w-1 h-1 bg-gold shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {m.audience && (
                  <span className="mt-6 inline-flex self-start text-[10px] font-700 uppercase tracking-[0.16em] text-gold border border-gold px-2 py-0.5">{m.audience}</span>
                )}
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-8">
            <p className="text-sm text-navy/70 max-w-2xl leading-relaxed">
              <span className="font-700 text-navy">Not sure which model fits?</span> Every practice below can be delivered as
              consulting, fractional, workforce, or a hybrid mix.
            </p>
            <Link to="/services" data-testid="delivery-explore" className="inline-flex items-center gap-2 bg-navy text-white font-700 px-6 py-3 text-sm hover:bg-gold hover:text-navy transition-colors group">
              Explore all practice areas <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FeatureCards() {
  return (
    <section className="bg-white relative -mt-1" data-testid="home-features">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <SectionHeading eyebrow="Practice Areas" title={<>Seven disciplines. One <span className="text-gold">strategic</span> partner.</>} />
          <Reveal delay={0.2}><Link to="/services" className="text-navy font-600 text-sm inline-flex items-center gap-2 group shrink-0 link-underline">View all services <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link></Reveal>
        </div>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {FEATURE_CARDS.map((f, i) => (
            <StaggerItem key={i}>
              <Link to={`/services/${f.slug}`} data-testid={`feature-card-${i}`} className="group relative bg-white p-10 h-full flex flex-col justify-between min-h-[280px] hover:bg-navy transition-colors duration-500">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 border border-slate-200 flex items-center justify-center group-hover:border-gold transition-colors">
                    <Icon name={f.icon} className="w-6 h-6 text-navy group-hover:text-gold transition-colors" strokeWidth={1.5} />
                  </div>
                  <span className="font-display font-800 text-4xl text-slate-100 group-hover:text-white/10 transition-colors">0{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-display font-700 text-xl text-navy group-hover:text-white transition-colors leading-tight">{f.label}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-600 text-gold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                    Explore <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="bg-lightgray py-24 md:py-32" data-testid="home-about">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal className="relative">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={IMAGES.executiveMeeting} alt="Executive consulting meeting" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-8 -right-4 sm:right-8 bg-navy text-white p-8 max-w-xs shadow-2xl">
            <p className="font-display font-800 text-4xl text-gold">100%</p>
            <p className="text-white/70 text-sm mt-2 leading-relaxed">Mission-focused engagements, engineered for measurable outcomes.</p>
          </div>
        </Reveal>
        <div>
          <SectionHeading
            eyebrow="Who We Are"
            title={<>A firm built by practitioners, for organizations that lead.</>}
            intro="Consilium Pro Workforce Solutions is a multidisciplinary consulting and fractional services firm providing organizations with strategic workforce, operational, administrative, technology, marketing, training, security, and business support solutions."
          />
          <div className="mt-8 space-y-4">
            {[
              "We provide the expertise of experienced professionals and functional leaders, without requiring you to build every capability in-house.",
              "Access those capabilities on a consulting, fractional, managed, or workforce basis, often blended inside a single engagement.",
              "Senior practitioners, government-ready delivery, customized to your context. Never templates.",
            ].map((t, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <div className="flex items-start gap-4">
                  <span className="mt-2 w-2 h-2 bg-gold shrink-0" />
                  <p className="text-gray-600 leading-relaxed">{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}><div className="mt-10"><PrimaryButton to="/about" testid="about-learn-more" variant="outline">Learn More</PrimaryButton></div></Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="bg-white py-24 md:py-32" data-testid="home-services">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="What We Do" title={<>Consulting services engineered for impact</>} intro="A full spectrum of advisory practices, from human capital to AI, government solutions to operational readiness." />
        <div className="mt-16 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 2) * 0.08}>
              <Link to={`/services/${s.slug}`} data-testid={`service-preview-${s.slug}`} className="group bg-white p-8 md:p-10 flex gap-6 h-full hover:bg-lightgray transition-colors duration-300">
                <div className="w-12 h-12 bg-navy flex items-center justify-center shrink-0 group-hover:bg-gold transition-colors">
                  <Icon name={s.icon} className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display font-700 text-lg text-navy leading-snug group-hover:text-gold transition-colors">{s.title}</h3>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{s.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustriesMarquee() {
  return (
    <section className="bg-navy py-16 overflow-hidden border-y border-white/10" data-testid="home-industries-marquee">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 mb-10">
        <Overline>Industries Served</Overline>
      </div>
      <Marquee speed={35} gradient gradientColor="#0F2D52" gradientWidth={120} autoFill>
        {INDUSTRIES.map((ind, i) => (
          <span key={i} className="mx-8 font-display font-700 text-2xl md:text-4xl tracking-tightest text-white/40 hover:text-gold transition-colors">
            {ind.name} <span className="text-gold/50 mx-4">/</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function WhySection() {
  return (
    <section className="bg-lightgray py-24 md:py-32" data-testid="home-why">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="Why Consilium Pro" title={<>The advantages of a true<br className="hidden md:block" /> strategic partner</>} />
        <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY.map((w, i) => (
            <StaggerItem key={i}>
              <div data-testid={`why-card-${i}`} className="group bg-white border border-slate-200 p-8 h-full hover:border-gold transition-colors duration-300">
                <div className="w-12 h-12 bg-navy flex items-center justify-center group-hover:bg-gold transition-colors">
                  <Icon name={w.icon} className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display font-700 text-lg text-navy">{w.title}</h3>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{w.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Testimonials() { return null; }

export default function Home() {
  return (
    <>
      <SEO
        title="Principal-led workforce solutions & consulting"
        description="Consilium Pro Workforce Solutions is an Atlanta-based, Principal-led consulting firm delivering workforce, HR, government readiness, technology, training, and operational solutions across the United States."
        path="/"
        image={IMAGES.executiveMeeting}
      />
      <Hero />
      <TrustBar />
      <DeliveryModels />
      <FeatureCards />
      <AboutPreview />
      <ServicesPreview />
      <IndustriesMarquee />
      <Practitioner />
      <HowItWorks />
      <WhySection />
      <CTABand />
    </>
  );
}
